import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { GrNext, GrPrevious } from "react-icons/gr";
import {
  TbBrandWhatsapp,
  TbCopy,
  TbDownload,
  TbEdit,
  TbEyeSearch,
  TbHistory,
  TbMailForward,
  TbPlus,
  TbRefresh,
  TbTrash,
} from "react-icons/tb";

import api from "../api/axios";
import "../assets/styles/questionnaires.css";
import "../assets/styles/directory-tables.css";

const PAGE_NAME = "Orders";
const PAGE_SIZE = 10;
const permissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [...new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  return pages.flatMap((page, index) =>
    index > 0 && page - pages[index - 1] > 1
      ? [`ellipsis-${page}`, page]
      : [page]
  );
};

const sectionNames = {
  couple: "The couple",
  overview: "Wedding overview",
  story: "Our story",
  family: "Family details",
  photos: "Photos",
  rsvp: "RSVP details",
  travel: "Travel and stay",
  gifting: "Gifting",
  website: "Website preferences",
  notes: "FAQs and notes",
};

const sectionFields = {
  couple: [
    "bride_full_name",
    "groom_full_name",
    "bride_nickname",
    "groom_nickname",
    "bride_contact_number",
    "groom_contact_number",
    "bride_email",
    "groom_email",
    "wedding_hashtag",
  ],
  overview: [
    "first_event_date",
    "last_event_date",
    "cities",
    "main_venue_name",
    "venue_address",
  ],
  story: [
    "how_you_met",
    "proposal",
    "milestone_1",
    "milestone_2",
    "milestone_3",
    "milestone_4",
  ],
  family: [
    "bride_parents",
    "groom_parents",
    "bride_siblings",
    "groom_siblings",
    "family_notes",
  ],
  photos: ["photo_folder_link", "homepage_hero_photos"],
  rsvp: [
    "deadline",
    "contact_person",
    "contact_number",
    "meal_preferences",
    "plus_ones",
    "kids_invited",
  ],
  travel: [
    "nearest_airport_station",
    "recommended_hotels",
    "group_booking_code",
    "shuttle_details",
    "travel_notes",
  ],
  gifting: ["registry_note"],
  website: [
    "preferred_url",
    "password_protected",
    "colour_palette",
    "languages",
    "background_music",
  ],
  notes: ["faqs", "anything_else"],
};

const eventFields = [
  "event_name",
  "date",
  "start_time",
  "venue_name",
  "venue_address",
  "dress_code_theme",
];

const longAnswerFields = new Set([
  "venue_address",
  "how_you_met",
  "proposal",
  "family_notes",
  "homepage_hero_photos",
  "recommended_hotels",
  "shuttle_details",
  "travel_notes",
  "registry_note",
  "faqs",
  "anything_else",
]);
const dateAnswerFields = new Set([
  "first_event_date",
  "last_event_date",
  "deadline",
  "date",
]);
const selectAnswerFields = {
  plus_ones: ["Yes, for all guests", "Only for select guests", "No"],
  kids_invited: ["Yes", "No", "Depends on the event"],
  password_protected: ["Yes", "No"],
};
const historyActionNames = {
  customer_update: "Customer updated questionnaire",
  customer_submit: "Customer submitted questionnaire",
  admin_update: "Admin updated questionnaire",
  admin_reopen: "Admin reopened questionnaire",
};

const parseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const humanise = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (value, includeTime = false) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const cloneAnswers = (value) =>
  JSON.parse(JSON.stringify(parseJson(value, {})));

const emptyEvent = () => ({
  event_name: "",
  date: "",
  start_time: "",
  venue_name: "",
  venue_address: "",
  dress_code_theme: "",
});

const getAnswerInputType = (field) => {
  if (dateAnswerFields.has(field)) return "date";
  if (field === "start_time") return "time";
  if (field.includes("email")) return "email";
  if (field.includes("contact_number")) return "tel";
  if (field === "photo_folder_link") return "url";
  return "text";
};

const formatHistoryField = (path) => {
  const parts = String(path || "").split(".");
  if (parts[0] === "events") {
    return `Event ${Number(parts[1]) + 1} · ${humanise(parts.slice(2).join(" "))}`;
  }
  const section = sectionNames[parts[0]] || humanise(parts[0]);
  return `${section} · ${humanise(parts.slice(1).join(" "))}`;
};

const displayHistoryValue = (value) => {
  if (value === "" || value === null || value === undefined) return "Blank";
  return String(value);
};

const Questionnaires = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search")?.trim() || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("answers");
  const [editAnswers, setEditAnswers] = useState({});
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchQuestionnaires = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get("/admin/orders/questionnaires", {
        headers: permissionHeaders("view"),
        params: {
          page,
          limit: PAGE_SIZE,
          status: status || undefined,
          search: debouncedSearch || undefined,
        },
      });
      setRows(response.data?.data || []);
      setPagination(
        response.data?.pagination || { page, total: 0, totalPages: 1 }
      );
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(error.response?.data?.message || "Questionnaires could not be loaded.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestionnaires(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, status]);

  const fetchHistory = async (row, page = 1, append = false) => {
    try {
      setHistoryLoading(true);
      const response = await api.get(
        `/admin/orders/${row.order_id}/questionnaire/history`,
        {
          headers: permissionHeaders("view"),
          params: { page, limit: 25 },
        }
      );
      const entries = response.data?.data || [];
      setHistory((current) => (append ? [...current, ...entries] : entries));
      setHistoryPagination(
        response.data?.pagination || { page, total: entries.length, totalPages: 1 }
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Questionnaire history could not be loaded."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const openQuestionnaire = async (row, edit = false) => {
    try {
      setActionId(row.order_questionnaire_id);
      const [questionnaireResponse, historyResponse] = await Promise.all([
        api.get(`/admin/orders/${row.order_id}/questionnaire`, {
          headers: permissionHeaders("view"),
        }),
        api.get(`/admin/orders/${row.order_id}/questionnaire/history`, {
          headers: permissionHeaders("view"),
          params: { page: 1, limit: 25 },
        }),
      ]);
      const questionnaire = questionnaireResponse.data?.data || row;
      setSelected(questionnaire);
      setEditAnswers(cloneAnswers(questionnaire.answers));
      setHistory(historyResponse.data?.data || []);
      setHistoryPagination(
        historyResponse.data?.pagination || {
          page: 1,
          total: 0,
          totalPages: 1,
        }
      );
      setViewMode(edit ? "edit" : "answers");
      setSidebarOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Questionnaire could not be opened.");
    } finally {
      setActionId(null);
    }
  };

  const startEditing = () => {
    setEditAnswers(cloneAnswers(selected?.answers));
    setViewMode("edit");
  };

  const updateEditSection = (section, field, value) => {
    setEditAnswers((current) => ({
      ...current,
      [section]: { ...(current[section] || {}), [field]: value },
    }));
  };

  const updateEditEvent = (index, field, value) => {
    setEditAnswers((current) => ({
      ...current,
      events: (current.events || []).map((event, eventIndex) =>
        eventIndex === index ? { ...event, [field]: value } : event
      ),
    }));
  };

  const saveAdminEdit = async () => {
    try {
      setSaving(true);
      const response = await api.patch(
        `/admin/orders/${selected.order_id}/questionnaire`,
        { answers: editAnswers },
        { headers: permissionHeaders("edit") }
      );
      const updated = response.data?.data || selected;
      setSelected(updated);
      setEditAnswers(cloneAnswers(updated.answers));
      setViewMode("answers");
      toast.success("Questionnaire updated.");
      await Promise.all([
        fetchHistory(updated),
        fetchQuestionnaires(pagination.page),
      ]);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Questionnaire could not be updated."
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadQuestionnaire = async (row) => {
    try {
      setDownloadingId(row.order_questionnaire_id);
      const response = await api.get(
        `/admin/orders/${row.order_id}/questionnaire/download`,
        {
          responseType: "blob",
          timeout: 60000,
          headers: permissionHeaders("view"),
        }
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `questionnaire-${
        row.order?.order_number || row.order_id
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Questionnaire PDF downloaded.");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Questionnaire PDF could not be downloaded."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const copyLink = async (row) => {
    try {
      await navigator.clipboard.writeText(row.customer_url);
      toast.success("Customer link copied.");
    } catch {
      toast.error("The link could not be copied. Please copy it from the details view.");
    }
  };

  const openWhatsApp = (row) => {
    const orderNo = row.order?.order_number || row.order_id;
    const message = `Please complete the wedding details for order ${orderNo} so we can begin production: ${row.customer_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  const resend = async (row) => {
    try {
      setActionId(row.order_questionnaire_id);
      await api.post(
        `/admin/orders/${row.order_id}/questionnaire/resend`,
        {},
        { headers: permissionHeaders("edit") }
      );
      toast.success("Customer notified again.");
      await fetchQuestionnaires(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.message || "Notification could not be sent.");
    } finally {
      setActionId(null);
    }
  };

  const confirmReopen = (row) => {
    confirmDialog({
      header: "Reopen Wedding Questionnaire",
      message: `Reopen the questionnaire for order ${
        row.order?.order_number || row.order_id
      } and notify the customer?`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Reopen and notify",
      rejectLabel: "Keep submitted",
      acceptClassName: "p-button-warning",
      accept: () => reopen(row),
    });
  };

  const reopen = async (row) => {
    try {
      setActionId(row.order_questionnaire_id);
      const response = await api.patch(
        `/admin/orders/${row.order_id}/questionnaire/reopen`,
        {},
        { headers: permissionHeaders("edit") }
      );
      toast.success("Questionnaire reopened.");
      const updated = response.data?.data || row;
      setSelected(updated);
      await Promise.all([
        fetchQuestionnaires(pagination.page),
        fetchHistory(updated),
      ]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Questionnaire could not be reopened.");
    } finally {
      setActionId(null);
    }
  };

  const renderAnswers = () => {
    const answers = parseJson(selected?.answers, {});
    return (
      <div className="questionnaire-answer-sections">
        {Object.entries(sectionNames).map(([sectionKey, sectionTitle]) => {
          const section = answers[sectionKey] || {};
          const entries = Object.entries(section).filter(([, value]) => value);
          return (
            <section key={sectionKey}>
              <h3>{sectionTitle}</h3>
              {entries.length ? (
                <div className="questionnaire-answer-grid">
                  {entries.map(([key, value]) => (
                    <div key={key}>
                      <span>{humanise(key)}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="questionnaire-empty-answer">No details supplied.</p>
              )}
            </section>
          );
        })}

        <section>
          <h3>Events schedule</h3>
          {(answers.events || []).length ? (
            <div className="questionnaire-event-list">
              {answers.events.map((event, index) => (
                <article key={`event-${index}`}>
                  <h4>Event {index + 1}: {event.event_name || "Untitled"}</h4>
                  <div className="questionnaire-answer-grid">
                    {Object.entries(event)
                      .filter(([, value]) => value)
                      .map(([key, value]) => (
                        <div key={key}>
                          <span>{humanise(key)}</span>
                          <strong>{value}</strong>
                        </div>
                      ))}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="questionnaire-empty-answer">No events supplied.</p>
          )}
        </section>
      </div>
    );
  };

  const renderEditField = (field, value, onChange) => {
    if (selectAnswerFields[field]) {
      return (
        <select value={value || ""} onChange={(event) => onChange(event.target.value)}>
          <option value="">Not selected</option>
          {selectAnswerFields[field].map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
    if (longAnswerFields.has(field)) {
      return (
        <textarea
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          rows="3"
        />
      );
    }
    return (
      <input
        type={getAnswerInputType(field)}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  };

  const renderEditor = () => (
    <div className="questionnaire-edit-form">
      {Object.entries(sectionNames).map(([sectionKey, sectionTitle]) => (
        <section key={sectionKey}>
          <h3>{sectionTitle}</h3>
          <div className="questionnaire-edit-grid">
            {sectionFields[sectionKey].map((field) => (
              <label
                key={field}
                className={longAnswerFields.has(field) ? "wide" : ""}
              >
                <span>{humanise(field)}</span>
                {renderEditField(
                  field,
                  editAnswers[sectionKey]?.[field],
                  (value) => updateEditSection(sectionKey, field, value)
                )}
              </label>
            ))}
          </div>
        </section>
      ))}

      <section>
        <h3>Events schedule</h3>
        <div className="questionnaire-edit-events">
          {(editAnswers.events || []).map((event, index) => (
            <article key={`edit-event-${index}`}>
              <div className="questionnaire-edit-event-heading">
                <strong>Event {index + 1}</strong>
                <button
                  type="button"
                  onClick={() =>
                    setEditAnswers((current) => ({
                      ...current,
                      events: (current.events || []).filter(
                        (_, eventIndex) => eventIndex !== index
                      ),
                    }))
                  }
                >
                  <TbTrash /> Remove
                </button>
              </div>
              <div className="questionnaire-edit-grid">
                {eventFields.map((field) => (
                  <label key={field}>
                    <span>{humanise(field)}</span>
                    {renderEditField(field, event[field], (value) =>
                      updateEditEvent(index, field, value)
                    )}
                  </label>
                ))}
              </div>
            </article>
          ))}
        </div>
        <button
          type="button"
          className="questionnaire-add-event"
          onClick={() =>
            setEditAnswers((current) => ({
              ...current,
              events: [...(current.events || []), emptyEvent()],
            }))
          }
        >
          <TbPlus /> Add event
        </button>
      </section>

      <div className="questionnaire-edit-actions">
        <button
          type="button"
          className="ghost-action bottonborder"
          onClick={() => {
            setEditAnswers(cloneAnswers(selected.answers));
            setViewMode("answers");
          }}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="primary-action"
          onClick={saveAdminEdit}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div className="questionnaire-history">
      <div className="questionnaire-history-summary">
        <TbHistory />
        <div>
          <strong>{historyPagination.total} recorded update(s)</strong>
          <span>Only changed fields are stored, not questionnaire copies.</span>
        </div>
      </div>
      {!historyLoading && !history.length && (
        <p className="questionnaire-empty-answer">
          No changes have been recorded yet. History starts after this feature is deployed.
        </p>
      )}
      {history.map((entry) => {
        const changes = parseJson(entry.changes, []);
        return (
          <article
            className="questionnaire-history-entry"
            key={entry.order_questionnaire_history_id}
          >
            <div className="questionnaire-history-heading">
              <div>
                <strong>{historyActionNames[entry.action] || humanise(entry.action)}</strong>
                <span>by {entry.actor_name || humanise(entry.actor_type)}</span>
              </div>
              <time>{formatDate(entry.updatedAt || entry.createdAt, true)}</time>
            </div>
            {entry.from_status !== entry.to_status && (
              <div className="questionnaire-status-change">
                Status: <b>{humanise(entry.from_status)}</b>
                <span>→</span>
                <b>{humanise(entry.to_status)}</b>
              </div>
            )}
            {changes.length > 0 && (
              <div className="questionnaire-history-changes">
                {changes.map((change) => (
                  <div key={change.field}>
                    <strong>{formatHistoryField(change.field)}</strong>
                    <div>
                      <span>{displayHistoryValue(change.old_value)}</span>
                      <b>→</b>
                      <span>{displayHistoryValue(change.new_value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>
        );
      })}
      {historyLoading && <div className="questionnaire-loading">Loading history…</div>}
      {historyPagination.page < historyPagination.totalPages && (
        <button
          type="button"
          className="questionnaire-load-history ghost-action bottonborder"
          onClick={() => fetchHistory(selected, historyPagination.page + 1, true)}
          disabled={historyLoading}
        >
          Load older updates
        </button>
      )}
    </div>
  );

  return (
    <section className="admin-page questionnaires-page">
      <ConfirmDialog />
      <div className="page-header questionnaires-header">
        <div>
          <p>Customer requirements</p>
          <h1>Wedding Questionnaires</h1>
          <span>Track, share and review post-order wedding details.</span>
        </div>
        <div className="page-total-summary">
          <span>Total:</span>
          <strong>{pagination.total}</strong>
        </div>
      </div>

      <div className="page-card questionnaire-list-card">
        <div className="questionnaire-toolbar">
          <div>
            <h2>Questionnaire Register</h2>
            <p>{loading ? "Loading questionnaires…" : `${pagination.total} questionnaire(s) found.`}</p>
          </div>
          <div className="questionnaire-table-filters">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search order number…"
            />
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="draft">Draft</option>
              <option value="reopened">Reopened</option>
              <option value="submitted">Submitted</option>
            </select>
          </div>
        </div>

        <div className={`directory-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="directory-data-table questionnaire-data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Placed</th>
                <th>Last saved</th>
                <th>Submitted</th>
                <th aria-label="Actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && !rows.length && (
                <tr>
                  <td className="directory-table-empty" colSpan="7">
                    <TbEyeSearch />
                    <strong>No questionnaires found</strong>
                    <span>Required questionnaires will appear after successful payment.</span>
                  </td>
                </tr>
              )}

              {rows.map((row) => {
                const customer = parseJson(row.order?.customer_snapshot, {});
                const busy = actionId === row.order_questionnaire_id;

                return (
                  <tr key={row.order_questionnaire_id}>
                    <td className="directory-person-cell">
                      <div className="directory-person">
                        <span className="directory-avatar">Q</span>
                        <div>
                          <strong>{row.order?.order_number || `Order #${row.order_id}`}</strong>
                          <span>Wedding questionnaire</span>
                          <small>Questionnaire #{row.order_questionnaire_id}</small>
                        </div>
                      </div>
                    </td>

                    <td className="questionnaire-customer-cell">
                      <strong>{customer.name || "Customer"}</strong>
                      <span>{customer.email || "No email available"}</span>
                    </td>

                    <td>
                      <span className={`questionnaire-status ${row.status}`}>{row.status}</span>
                    </td>

                    <td className="directory-date-cell">
                      <strong>{formatDate(row.order?.createdAt)}</strong>
                      <span>Order placed</span>
                    </td>

                    <td className="directory-date-cell questionnaire-date-time">
                      <strong>{formatDate(row.last_saved_at, true)}</strong>
                      <span>Latest response</span>
                    </td>

                    <td className="directory-date-cell questionnaire-date-time">
                      <strong>{formatDate(row.submitted_at, true)}</strong>
                      <span>{row.submitted_at ? "Customer submitted" : "Not submitted"}</span>
                    </td>

                    <td className="directory-actions-cell questionnaire-actions-cell">
                      <div className="directory-row-actions">
                        <button type="button" title="View questionnaire" aria-label="View questionnaire" onClick={() => openQuestionnaire(row)} disabled={busy}><TbEyeSearch /></button>
                        <button type="button" title="Edit questionnaire" aria-label="Edit questionnaire" onClick={() => openQuestionnaire(row, true)} disabled={busy}><TbEdit /></button>
                        <button type="button" title="Download questionnaire PDF" aria-label="Download questionnaire PDF" onClick={() => downloadQuestionnaire(row)} disabled={downloadingId === row.order_questionnaire_id}><TbDownload /></button>
                        <button type="button" title="Copy customer link" aria-label="Copy customer link" onClick={() => copyLink(row)}><TbCopy /></button>
                        <button type="button" title="Share on WhatsApp" aria-label="Share on WhatsApp" onClick={() => openWhatsApp(row)}><TbBrandWhatsapp /></button>
                        {row.status !== "submitted" && (
                          <button type="button" title="Resend notification" aria-label="Resend notification" onClick={() => resend(row)} disabled={busy}><TbMailForward /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination" style={{ marginTop: "15px" }}>
          <button type="button" className="directory-pagination-direction" disabled={pagination.page <= 1 || loading} onClick={() => fetchQuestionnaires(pagination.page - 1)}><GrPrevious /> Previous</button>
          <div className="directory-pagination-pages">
            {getPaginationItems(pagination.page, pagination.totalPages).map((item) =>
              typeof item === "string" ? (
                <span key={item}>…</span>
              ) : (
                <button
                  type="button"
                  className={item === pagination.page ? "active" : ""}
                  key={item}
                  disabled={loading}
                  aria-label={`Go to page ${item}`}
                  aria-current={item === pagination.page ? "page" : undefined}
                  onClick={() => fetchQuestionnaires(item)}
                >
                  {item}
                </button>
              )
            )}
          </div>
          <button type="button" className="directory-pagination-direction" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => fetchQuestionnaires(pagination.page + 1)}>Next <GrNext /></button>
        </div>
      </div>

      <Sidebar
        visible={sidebarOpen}
        position="right"
        onHide={() => {
          if (saving) return;
          setSidebarOpen(false);
          setViewMode("answers");
        }}
        className="questionnaire-view-sidebar"
      >
        {selected && (
          <div className="questionnaire-view-shell">
            <div className="questionnaire-view-heading">
              <div>
                <span>Wedding questionnaire</span>
                <h2>{selected.order?.order_number}</h2>
                <p>Submitted {formatDate(selected.submitted_at, true)}</p>
              </div>
              <button
                type="button"
                onClick={() => downloadQuestionnaire(selected)}
                disabled={downloadingId === selected.order_questionnaire_id}
              >
                <TbDownload />
                {downloadingId === selected.order_questionnaire_id
                  ? "Preparing…"
                  : "Download PDF"}
              </button>
            </div>
            <div className="questionnaire-share-box">
              <input value={selected.customer_url || ""} readOnly />
              <button type="button" onClick={() => copyLink(selected)}><TbCopy /> Copy</button>
              <button type="button" onClick={() => openWhatsApp(selected)}><TbBrandWhatsapp /> WhatsApp</button>
            </div>
            {viewMode !== "edit" && (
              <div className="questionnaire-view-tabs">
                <button
                  type="button"
                  className={viewMode === "answers" ? "active" : ""}
                  onClick={() => setViewMode("answers")}
                >
                  <TbEyeSearch /> Answers
                </button>
                <button
                  type="button"
                  className={viewMode === "history" ? "active" : ""}
                  onClick={() => setViewMode("history")}
                >
                  <TbHistory /> History ({historyPagination.total})
                </button>
              </div>
            )}
            {viewMode === "answers" && renderAnswers()}
            {viewMode === "history" && renderHistory()}
            {viewMode === "edit" && renderEditor()}
            {viewMode !== "edit" && (
              <div className="questionnaire-view-actions">
                <button type="button" className="secondary-action" onClick={startEditing}>
                  <TbEdit /> Edit questionnaire
                </button>
                {selected.status === "submitted" && (
                  <button type="button" className="secondary-action" onClick={() => confirmReopen(selected)} disabled={actionId === selected.order_questionnaire_id}>
                    <TbRefresh /> Reopen for customer
                  </button>
                )}
                {selected.status !== "submitted" && (
                  <button type="button" className="primary-action" onClick={() => resend(selected)} disabled={actionId === selected.order_questionnaire_id}>
                    <TbMailForward /> Resend notification
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default Questionnaires;
